import { isUndefined } from 'lodash';
import axios from 'src/utils/axios';
import logger from 'src/utils/logger';
import parse from 'src/utils/parse';

import { normalizeSolutionSlug } from 'src/mindtime/config/subscriptionPermissionRegistry';

export type SubscriptionEntityType = 'PRODUCT' | 'PACKAGE';

export type SubscriptionTypeApi = 'MONTHLY' | 'YEARLY';

export type SubscriptionLicense = {
  id: string;
  subscriptionId?: string;
  clientId?: string;
  entityType: SubscriptionEntityType;
  productId: string;
  subscriptionType: SubscriptionTypeApi;
  licenseKey: string;
  createdAt?: string;
  createdBy?: string;
};

/** Optional on older records that were created before keys were minted. */
export type SubscriptionRecord = {
  id?: string;
  licenses?: SubscriptionLicense[];
};

/** Paginated licenses on get-by-id / create / update (`licenses.data`). */
export type LicensesPage = {
  items: SubscriptionLicense[];
  /** 1-based page from BE. */
  page: number;
  pageSize: number;
  totalRecords: number;
  totalPages: number;
};

export type GetSubscriptionByIdParams = {
  /** 1-based page of licenses. */
  page?: number;
  /** Page size (same query name as list getAll). */
  size?: number;
};

export type CreateSubscriptionProductPayload = {
  entityType: SubscriptionEntityType;
  productId: string;
  numberOfLicenses: number;
};

export type CreateSubscriptionPayload = {
  /** Omit to let BE auto-generate; if sent, BE uses this value. */
  subscriptionOrderNumber?: string;
  customerReference?: string;
  subscriptionType: SubscriptionTypeApi;
  clientId: string;
  startDate: string;
  internalNotes?: string;
  products: CreateSubscriptionProductPayload[];
};

export type ListSubscriptionsParams = {
  page?: number;
  size?: number;
  search?: string;
  status?: string;
  clientId?: string;
  resellerId?: string;
  entityType?: string;
};

/** Org subscription entitlements — flattened from BE `products[]`. */
export type OrgSubscriptionPermissions = {
  organizationId: string;
  hasSubscriptions: boolean;
  /** Normalized solution slugs from BE `solution` (e.g. `edr`). */
  subscribedSolutions: string[];
  /** Normalized catalog domain slugs from BE `domain` (e.g. `observability`). */
  subscribedDomains: string[];
  /** Normalized catalog category slugs from BE `category` (e.g. `security`). */
  subscribedCategories: string[];
  /** Normalized permission identifiers entitled to the org. */
  subscriptionKeys: string[];
};

const emptyOrgSubscriptionPermissions = (
  orgId = '',
): OrgSubscriptionPermissions => ({
  organizationId: orgId,
  hasSubscriptions: false,
  subscribedSolutions: [],
  subscribedDomains: [],
  subscribedCategories: [],
  subscriptionKeys: [],
});

const READ_SUFFIX = '-read';
const CREATE_SUFFIX = '-create';

const addNormalizedSubscriptionKey = (keys: Set<string>, raw: string): void => {
  const lower = String(raw ?? '')
    .trim()
    .toLowerCase();
  if (!lower.endsWith(READ_SUFFIX) && !lower.endsWith(CREATE_SUFFIX)) return;

  keys.add(lower);
};

/** Normalize BE permission identifiers once at parse time. */
const mapPermissionIdList = (permissions: unknown): string[] => {
  if (!Array.isArray(permissions)) return [];
  const keys = new Set<string>();
  permissions.forEach((id) =>
    addNormalizedSubscriptionKey(keys, String(id ?? '')),
  );
  return Array.from(keys);
};

const parseOrgSubscriptionPermissions = (
  response: unknown,
  fallbackOrgId = '',
): OrgSubscriptionPermissions => {
  const root = asRecord(response);
  const body = asRecord(root?.data) ?? root;
  if (!body) return emptyOrgSubscriptionPermissions(fallbackOrgId);

  const organizationId = String(
    body.organizationId || fallbackOrgId || '',
  ).trim();
  const solutionSlugs = new Set<string>();
  const domainSlugs = new Set<string>();
  const categorySlugs = new Set<string>();
  const subscriptionKeys = new Set<string>();

  (Array.isArray(body.products) ? body.products : []).forEach((raw) => {
    const row = asRecord(raw);
    if (!row) return;

    const solution = normalizeSolutionSlug(String(row.solution || ''));
    if (solution) solutionSlugs.add(solution);

    const domain = normalizeSolutionSlug(String(row.domain || ''));
    if (domain) domainSlugs.add(domain);

    const category = normalizeSolutionSlug(String(row.category || ''));
    if (category) categorySlugs.add(category);

    mapPermissionIdList(row.permissions).forEach((key) =>
      subscriptionKeys.add(key),
    );
  });

  const subscribedSolutions = Array.from(solutionSlugs);
  const keys = Array.from(subscriptionKeys);
  const hasSubscriptions = Boolean(body.hasSubscriptions) || keys.length > 0;

  return {
    organizationId,
    hasSubscriptions,
    subscribedSolutions,
    subscribedDomains: Array.from(domainSlugs),
    subscribedCategories: Array.from(categorySlugs),
    subscriptionKeys: keys,
  };
};

export { parseOrgSubscriptionPermissions };

const baseUrl = (): string => {
  const url = process.env.REACT_APP_SUBSCRIPTION;
  if (!url) {
    throw new Error(
      'REACT_APP_SUBSCRIPTION environment variable is not defined',
    );
  }
  return url.replace(/\/$/, '');
};

/** Parse list envelopes: array | { data } | { content } | nested total fields. */
export const parseSubscriptionsList = (
  response: any,
): { items: any[]; total: number } => {
  const body = response?.data;
  if (Array.isArray(body)) {
    return { items: body, total: body.length };
  }
  if (!body || typeof body !== 'object') {
    return { items: [], total: 0 };
  }

  const items = Array.isArray(body.data)
    ? body.data
    : Array.isArray(body.content)
      ? body.content
      : Array.isArray(body.items)
        ? body.items
        : [];

  const total =
    body.totalRecords ??
    body.totalElements ??
    body.total ??
    body.count ??
    items.length;

  return { items, total: Number(total) || 0 };
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;

/** GET list / get-by-id: `client` and `reseller` are `{ id, name }`. */
export type NamedParty = { id: string; name: string };

export const parseNamedParty = (value: unknown): NamedParty | null => {
  const row = asRecord(value);
  if (!row) return null;
  const id = String(row.id || row._id || '').trim();
  if (!id) return null;
  const name = String(row.name || row.organizationName || '').trim();
  return { id, name };
};

export const namedPartyFromSubscription = (
  party: unknown,
  fallbackId?: unknown,
): NamedParty | null => {
  const nested = parseNamedParty(party);
  if (nested) return nested;
  const id = typeof fallbackId === 'string' ? fallbackId.trim() : '';
  return id ? { id, name: '' } : null;
};

const unwrapSubscriptionObject = (
  response: unknown,
): Record<string, unknown> | null => {
  const root = asRecord(response);
  const body = asRecord(root?.data) ?? root;
  if (!body) return null;
  const nested = asRecord(body.data);
  if (
    nested &&
    (nested.licenses != null ||
      nested.id ||
      nested.products ||
      nested.licenseCount != null)
  ) {
    return nested;
  }
  return body;
};

const EMPTY_LICENSES_PAGE: LicensesPage = {
  items: [],
  page: 1,
  pageSize: 25,
  totalRecords: 0,
  totalPages: 0,
};

const parseLicenseRows = (source: unknown[]): SubscriptionLicense[] =>
  source
    .map((entry) => {
      const row = asRecord(entry);
      if (!row) return null;
      const licenseKey = String(row.licenseKey || '').trim();
      const productId = String(row.productId || '').trim();
      if (!licenseKey || !productId) return null;
      const entityType =
        (String(row.entityType) || '').toUpperCase() === 'PACKAGE'
          ? 'PACKAGE'
          : 'PRODUCT';
      const subscriptionType =
        String(row.subscriptionType || '').toUpperCase() === 'YEARLY'
          ? 'YEARLY'
          : 'MONTHLY';
      return {
        id: String(row.id || row._id || licenseKey),
        subscriptionId: row.subscriptionId
          ? String(row.subscriptionId)
          : undefined,
        clientId: row.clientId ? String(row.clientId) : undefined,
        entityType,
        productId,
        subscriptionType,
        licenseKey,
        createdAt: row.createdAt ? String(row.createdAt) : undefined,
        createdBy:
          typeof row.createdBy === 'string'
            ? row.createdBy
            : asRecord(row.createdBy)?.id
              ? String(asRecord(row.createdBy)?.id)
              : undefined,
      } satisfies SubscriptionLicense;
    })
    .filter(Boolean) as SubscriptionLicense[];

/**
 * BE shape: `licenses: { data, page, pageSize, totalRecords, totalPages }`.
 * Also accepts a legacy bare array.
 */
export const parseLicensesPage = (raw: unknown): LicensesPage => {
  if (Array.isArray(raw)) {
    const items = parseLicenseRows(raw);
    return {
      items,
      page: 1,
      pageSize: items.length || 25,
      totalRecords: items.length,
      totalPages: items.length ? 1 : 0,
    };
  }

  const envelope = asRecord(raw);
  if (!envelope) return { ...EMPTY_LICENSES_PAGE };

  const source = Array.isArray(envelope.data)
    ? envelope.data
    : Array.isArray(envelope.licenses)
      ? envelope.licenses
      : [];
  const items = parseLicenseRows(source);
  const totalRecords =
    Number(envelope.totalRecords ?? envelope.total ?? items.length) || 0;
  const pageSize = Number(envelope.pageSize ?? envelope.size ?? 25) || 25;
  const page = Number(envelope.page ?? 1) || 1;
  const totalPages =
    Number(envelope.totalPages) ||
    (pageSize > 0 ? Math.ceil(totalRecords / pageSize) : 0) ||
    0;

  return { items, page, pageSize, totalRecords, totalPages };
};

export const parseSubscriptionLicenses = (
  raw: unknown,
): SubscriptionLicense[] => parseLicensesPage(raw).items;

/** Read paginated `licenses` from create / get / patch envelopes. */
export const parseLicensesPageFromResponse = (
  response: unknown,
): LicensesPage => {
  const record = unwrapSubscriptionObject(response);
  return parseLicensesPage(record?.licenses ?? asRecord(response)?.licenses);
};

/** License rows only (first/current page). */
export const parseLicensesFromResponse = (
  response: unknown,
): SubscriptionLicense[] => parseLicensesPageFromResponse(response).items;

export const parseSubscriptionFromResponse = (
  response: unknown,
): Record<string, unknown> | null => unwrapSubscriptionObject(response);

class SubscriptionsApi {
  async getSubscriptions(params: ListSubscriptionsParams = {}): Promise<any> {
    return new Promise((resolve, reject) => {
      const query: Record<string, string | number> = {};
      if (params.page != null) query.page = params.page;
      if (params.size != null) query.size = params.size;
      if (params.search) query.search = params.search;
      if (params.status) query.status = params.status;
      if (params.clientId) query.clientId = params.clientId;
      if (params.resellerId) query.resellerId = params.resellerId;
      if (params.entityType) query.entityType = params.entityType;

      axios
        .get(`${baseUrl()}/subscriptions`, { params: query })
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('Internal Server Error'));
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async createSubscription(payload: CreateSubscriptionPayload): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .post(`${baseUrl()}/subscriptions`, payload)
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('Internal Server Error'));
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async getSubscriptionById(
    id: string,
    params: GetSubscriptionByIdParams = {},
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      const query: Record<string, number> = {};
      if (params.page != null) query.page = params.page;
      if (params.size != null) query.size = params.size;

      axios
        .get(`${baseUrl()}/subscriptions/${id}`, { params: query })
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('Internal Server Error'));
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async updateSubscription(
    id: string,
    payload: Partial<CreateSubscriptionPayload>,
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .patch(`${baseUrl()}/subscriptions/${id}`, payload)
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('Internal Server Error'));
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async deleteSubscription(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .delete(`${baseUrl()}/subscriptions/${id}`)
        .then((response) => {
          response = parse(response);
          resolve(response);
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async extendSubscription(
    id: string,
    payload?: Record<string, unknown>,
  ): Promise<any> {
    return new Promise((resolve, reject) => {
      axios
        .post(`${baseUrl()}/subscriptions/${id}/extend`, payload ?? {})
        .then((response) => {
          response = parse(response);
          if (!isUndefined(response?.data)) {
            resolve(response);
          } else {
            reject(new Error('Internal Server Error'));
          }
        })
        .catch((error) => {
          logger(error, 'error');
          reject(error);
        });
    });
  }

  async getOrgPermissions(orgId: string): Promise<OrgSubscriptionPermissions> {
    const trimmedOrgId = String(orgId || '').trim();
    if (!trimmedOrgId) {
      return emptyOrgSubscriptionPermissions();
    }

    try {
      const response = await axios.get(
        `${baseUrl()}/subscriptions/org/${encodeURIComponent(trimmedOrgId)}/permissions`,
      );
      return parseOrgSubscriptionPermissions(parse(response), trimmedOrgId);
    } catch (error) {
      logger(error, 'error', 'subscriptionsApi.getOrgPermissions');
      throw error;
    }
  }
}

export const subscriptionsApi = new SubscriptionsApi();
